import React, { useCallback, useEffect, useState } from "react";
import Chip from "@mui/material/Chip";
import {
    AdvancedMarker,
    AdvancedMarkerRef,
    useAdvancedMarkerRef,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { MapPointProps } from "./map";

interface MarkerChipProps {
    point: MapPointProps;
    onSelect: (id: string) => void;
    onHover?: (id: string | null) => void;
    setMarkerRef?: (m: AdvancedMarkerRef | null) => void;
    className?: string;
}

export const MarkerChip = ({
    point,
    setMarkerRef,
    onSelect,
    onHover,
    className = "",
}: MarkerChipProps) => {
    const [hovered, setHovered] = useState(false);

    const {
        coords,
        icon,
        text,
        fullText,
        textColorClass,
        textBgColorClass,
        id,
        highlight,
    } = point;

    const onClick = useCallback(() => {
        if (typeof onSelect === "function") {
            onSelect(id);
        }
    }, [onSelect, id]);

    return (
        <AdvancedMarker
            position={coords}
            ref={setMarkerRef}
            onClick={onClick}
            onMouseEnter={() => {
                setHovered(true);
                onHover?.(id);
            }}
            onMouseLeave={() => {
                setHovered(false);
                onHover?.(null);
            }}
        >
            <Chip
                data-id={id}
                icon={icon}
                label={hovered || highlight ? fullText ?? text : text}
                className={[
                    "marker-tag",
                    !!textColorClass ? `text-${textColorClass}` : null,
                    !!textBgColorClass ? `bg-${textBgColorClass}` : null,
                    className,
                ]
                    .filter(Boolean)
                    .join(" ")}
                sx={highlight ? {
                    transform: "scale(1.15)",
                    boxShadow: 3,
                    zIndex: 10,
                } : undefined}
            />
        </AdvancedMarker>
    );
};

interface ClusteredMarkerChipOwnProps {
    clusterer: MarkerClusterer;
}

type ClusteredMarkerChipProps = ClusteredMarkerChipOwnProps & MarkerChipProps;

export const ClusteredMarkerChip = ({
    clusterer,
    ...rest
}: ClusteredMarkerChipProps) => {
    const [markerRef, marker] = useAdvancedMarkerRef();

    useEffect(() => {
        if (!marker) return;

        clusterer?.addMarker(marker);

        return () => {
            clusterer?.removeMarker(marker);
        };
    }, [marker, clusterer]);

    return <MarkerChip setMarkerRef={markerRef} {...rest} />;
};
